import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { agencyOverview, listUsers, inviteUser, updateUser, deleteUser, resendInvite, fmtMoney, fmtNum } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Buildings, CurrencyDollar, Megaphone, ListChecks, PlugsConnected, Sparkle, CalendarStar,
  UserPlus, Warning, ClockCounterClockwise, MagnifyingGlass, Trash, ArrowRight, UsersThree,
  PencilSimple, PaperPlaneTilt, Prohibit, CheckCircle, Scroll, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DOT = { green: "bg-emerald-400", yellow: "bg-amber-400", red: "bg-red-500" };

export const ROLE_LABELS = {
  super_admin: "Super Admin", agency_admin: "Agency Admin", agency_staff: "Agency Staff",
  workspace_owner: "Workspace Owner", marketing_manager: "Marketing Manager", marketing_staff: "Marketing Staff",
  office_staff: "Office Staff", sales: "Sales", read_only: "Read Only",
  client_owner: "Client Owner", client_staff: "Client Staff",
};
// Roles that span every workspace (no per-workspace assignment needed).
const AGENCY_ROLES = new Set(["super_admin", "agency_admin", "agency_staff"]);
const ROLE_ORDER = ["super_admin", "agency_admin", "workspace_owner", "marketing_manager", "marketing_staff", "office_staff", "sales", "read_only", "client_owner", "client_staff"];

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-matte p-4" data-testid={`agency-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 label-caps"><Icon size={13} className={accent ? "brand-text" : "text-[#9a9a9a]"} weight="bold" /> {label}</div>
      <div className="font-head text-xl md:text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}

function WorkspacePicker({ workspaces, selected, onToggle }) {
  return (
    <div className="space-y-1.5 max-h-44 overflow-y-auto rounded-lg border border-white/10 bg-[#10101A] p-2.5" data-testid="workspace-picker">
      {workspaces.map((w) => (
        <label key={w.client_id} className="flex items-center gap-2.5 text-sm cursor-pointer py-0.5">
          <Checkbox data-testid={`ws-${w.client_id}`} checked={selected.includes(w.client_id)} onCheckedChange={() => onToggle(w.client_id)}
            className="border-white/30 data-[state=checked]:bg-[#6B3DFF] data-[state=checked]:border-[#6B3DFF]" />
          <span className="text-[#cfcfcf]">{w.name}</span>
        </label>
      ))}
      {!workspaces.length && <div className="text-xs text-[#7a7a7a]">No workspaces yet.</div>}
    </div>
  );
}

function InviteDialog({ workspaces }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("client_owner");
  const [ws, setWs] = useState([]);
  const agencyWide = AGENCY_ROLES.has(role);
  const m = useMutation({
    mutationFn: () => inviteUser({ email, name, role, workspace_ids: agencyWide ? [] : ws }),
    onSuccess: (r) => {
      toast.success(r.email_sent ? `Invitation email sent to ${email}` : "Invitation created"); qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false); setEmail(""); setName(""); setWs([]);
      navigator.clipboard?.writeText(window.location.origin + r.invite_link).then(() => toast.message("Invite link copied to clipboard")).catch(() => {});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Invite failed"),
  });
  const toggle = (id) => setWs((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);
  return (
    <>
      <Button data-testid="invite-user-btn" onClick={() => setOpen(true)} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><UserPlus size={16} className="mr-1.5" /> Invite User</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-head">Invite a user</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">They'll get a link to set their password. Users only ever see workspaces assigned to them.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">Email</Label><Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div><Label className="label-caps">Name</Label><Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div><Label className="label-caps">Role</Label>
              <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm mt-1">
                {ROLE_ORDER.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select></div>
            {agencyWide
              ? <div className="text-xs text-[#7a7a7a] bg-white/[0.03] rounded-lg p-2.5">This is an agency-wide role with access to <span className="brand-text">all workspaces</span>.</div>
              : <div><Label className="label-caps">Assigned workspaces</Label><WorkspacePicker workspaces={workspaces} selected={ws} onToggle={toggle} /></div>}
          </div>
          <DialogFooter><Button data-testid="invite-send" disabled={!email || (!agencyWide && !ws.length) || m.isPending} onClick={() => m.mutate()} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Send Invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UserEditDialog({ user, workspaces, open, setOpen }) {
  const qc = useQueryClient();
  const [role, setRole] = useState(user.role);
  const [ws, setWs] = useState(user.workspace_ids || []);
  const agencyWide = AGENCY_ROLES.has(role);
  const m = useMutation({
    mutationFn: () => updateUser(user.id, { role, workspace_ids: agencyWide ? [] : ws }),
    onSuccess: () => { toast.success("User updated"); qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const toggle = (id) => setWs((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#14141F] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-head">Edit {user.name || user.email}</DialogTitle>
          <DialogDescription className="text-[#7a7a7a]">Change role & workspace access. Permissions follow the role's defaults.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-caps">Role</Label>
            <select data-testid="edit-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm mt-1">
              {ROLE_ORDER.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select></div>
          {agencyWide
            ? <div className="text-xs text-[#7a7a7a] bg-white/[0.03] rounded-lg p-2.5">Agency-wide role — access to all workspaces.</div>
            : <div><Label className="label-caps">Assigned workspaces</Label><WorkspacePicker workspaces={workspaces} selected={ws} onToggle={toggle} /></div>}
        </div>
        <DialogFooter><Button data-testid="edit-save" disabled={m.isPending} onClick={() => m.mutate()} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, workspaces }) {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const inv = () => qc.invalidateQueries({ queryKey: ["users"] });
  const delUser = useMutation({ mutationFn: () => deleteUser(u.id), onSuccess: () => { inv(); toast.success("User removed"); }, onError: (e) => toast.error(e?.response?.data?.detail || "Failed") });
  const toggleStatus = useMutation({
    mutationFn: () => updateUser(u.id, { status: u.status === "disabled" ? "active" : "disabled" }),
    onSuccess: () => { inv(); toast.success(u.status === "disabled" ? "User enabled" : "User disabled"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const resend = useMutation({
    mutationFn: () => resendInvite(u.id),
    onSuccess: (r) => { navigator.clipboard?.writeText(window.location.origin + r.invite_link).then(() => {}).catch(() => {}); toast.success(r.email_sent ? "Invitation email re-sent" : "New invite link copied to clipboard"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const statusColor = u.status === "active" ? "text-emerald-400" : u.status === "invited" ? "text-amber-300" : "text-red-400";
  return (
    <div data-testid={`user-row-${u.id}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/8">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#cfcfcf] truncate">{u.display_name || u.name} <span className="text-[#7a7a7a]">· {u.email}</span></div>
        <div className="text-[11px] text-[#7a7a7a]">
          <span className="brand-text">{ROLE_LABELS[u.role] || u.role}</span> · <span className={statusColor}>{u.status}</span>
          {AGENCY_ROLES.has(u.role) ? " · all workspaces" : u.workspace_ids?.length ? ` · ${u.workspace_ids.length} workspace(s)` : " · no workspace"}
        </div>
      </div>
      {u.status === "invited" && <button data-testid={`resend-${u.id}`} title="Resend invite" onClick={() => resend.mutate()} className="text-[#7a7a7a] hover:text-sky-400"><PaperPlaneTilt size={14} /></button>}
      {u.role !== "super_admin" && <button data-testid={`edit-${u.id}`} title="Edit" onClick={() => setEditOpen(true)} className="text-[#7a7a7a] hover:text-white"><PencilSimple size={14} /></button>}
      {u.role !== "super_admin" && <button data-testid={`toggle-${u.id}`} title={u.status === "disabled" ? "Enable" : "Disable"} onClick={() => toggleStatus.mutate()} className="text-[#7a7a7a] hover:text-amber-400">{u.status === "disabled" ? <CheckCircle size={14} /> : <Prohibit size={14} />}</button>}
      {u.role !== "super_admin" && <button data-testid={`delete-user-${u.id}`} title="Delete" onClick={() => delUser.mutate()} className="text-[#7a7a7a] hover:text-red-400"><Trash size={14} /></button>}
      {editOpen && <UserEditDialog user={u} workspaces={workspaces} open={editOpen} setOpen={setEditOpen} />}
    </div>
  );
}

export default function AgencyDashboardPage() {
  const navigate = useNavigate();
  const { setActive } = useClient();
  const [search, setSearch] = useState("");

  const ov = useQuery({ queryKey: ["agency-overview"], queryFn: agencyOverview });
  const usersQ = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const k = ov.data?.kpis || {};
  const clients = (ov.data?.clients || []).filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  const workspaces = ov.data?.clients || [];

  const openWorkspace = (id) => { setActive(id); navigate("/"); };

  return (
    <PageContainer>
      <PageHeader eyebrow="Agency" title="Agency Command Center"
        subtitle="Manage every client workspace, staff member and permission from one place.">
        <div className="flex items-center gap-2">
          <Button data-testid="goto-audit" variant="outline" onClick={() => navigate("/audit")} className="border-white/10 bg-transparent text-[#cfcfcf] hover:text-white"><Scroll size={16} className="mr-1.5" /> Audit Logs</Button>
          <InviteDialog workspaces={workspaces} />
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-7" data-testid="agency-kpis">
        <Kpi label="Total Clients" value={fmtNum(k.total_clients)} icon={Buildings} accent />
        <Kpi label="MRR" value={fmtMoney(k.mrr)} icon={CurrencyDollar} />
        <Kpi label="Managed Ad Spend" value={fmtMoney(k.managed_ad_spend)} icon={Megaphone} accent />
        <Kpi label="Managed Revenue" value={fmtMoney(k.managed_revenue)} icon={CurrencyDollar} />
        <Kpi label="Pending Approvals" value={fmtNum(k.pending_approvals)} icon={ListChecks} accent />
        <Kpi label="Disconnected Accounts" value={fmtNum(k.disconnected_accounts)} icon={PlugsConnected} />
        <Kpi label="AI Tasks Waiting" value={fmtNum(k.ai_tasks_waiting)} icon={Sparkle} accent />
        <Kpi label="Scheduled Campaigns" value={fmtNum(k.scheduled_campaigns)} icon={CalendarStar} />
        <Kpi label="New Leads" value={fmtNum(k.new_leads)} icon={UsersThree} accent />
        <Kpi label="Team Members" value={fmtNum((usersQ.data || []).length)} icon={ShieldCheck} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 card-matte p-5" data-testid="client-list">
          <div className="flex items-center justify-between mb-4">
            <div className="label-caps flex items-center gap-1"><Buildings size={13} className="brand-text" /> Clients</div>
            <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7a7a7a]" />
              <Input data-testid="client-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="bg-[#10101A] border-white/10 pl-8 h-9 w-52" /></div>
          </div>
          <div className="space-y-2">
            {clients.map((c) => (
              <button key={c.client_id} data-testid={`client-row-${c.client_id}`} onClick={() => openWorkspace(c.client_id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/8 hover:border-[#6B3DFF]/40 hover:bg-white/[0.04] transition-colors text-left">
                <span className={`h-2.5 w-2.5 rounded-full ${DOT[c.status]} shrink-0`} />
                {c.logo_url && <img src={c.logo_url} alt={`${c.name} logo`} data-testid={`agency-client-logo-${c.client_id}`} className="h-7 w-7 rounded object-contain shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-[11px] text-[#7a7a7a]">{c.industry || "—"} · {c.connected}/4 accounts · {c.plan}</div>
                </div>
                <div className="text-right hidden sm:block"><div className="text-sm font-semibold">{fmtMoney(c.revenue)}</div><div className="text-[10px] text-[#7a7a7a]">{c.roas}x ROAS · {c.pending_approvals} pending</div></div>
                <ArrowRight size={15} className="text-[#7a7a7a] shrink-0" />
              </button>
            ))}
            {!clients.length && <div className="text-sm text-[#7a7a7a] text-center py-6">No clients match.</div>}
          </div>
        </div>

        <div className="space-y-5">
          <div className="card-matte p-5" data-testid="agency-alerts">
            <div className="label-caps mb-3 flex items-center gap-1"><Warning size={13} className="text-amber-400" /> Alerts</div>
            <div className="space-y-2">
              {(ov.data?.alerts || []).map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs"><span className={`h-1.5 w-1.5 rounded-full mt-1 shrink-0 ${a.level === "high" ? "bg-red-500" : "bg-amber-400"}`} /><span className="text-[#cfcfcf]">{a.text}</span></div>
              ))}
              {!(ov.data?.alerts || []).length && <div className="text-xs text-emerald-400">All systems healthy.</div>}
            </div>
          </div>
          <div className="card-matte p-5" data-testid="agency-recent">
            <div className="label-caps mb-3 flex items-center gap-1"><ClockCounterClockwise size={13} className="brand-text" /> Recent Activity</div>
            <div className="space-y-2">
              {(ov.data?.recent_activity || []).slice(0, 7).map((r, i) => (
                <div key={i} className="text-xs"><span className="text-[#cfcfcf]">{r.title}</span> <span className="text-[#7a7a7a]">· {r.client} · {r.status}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card-matte p-5 mt-5" data-testid="team-list">
        <div className="label-caps mb-3 flex items-center gap-1"><UsersThree size={13} className="brand-text" /> Team & Client Users ({(usersQ.data || []).length})</div>
        <div className="space-y-2">
          {(usersQ.data || []).map((u) => <UserRow key={u.id} u={u} workspaces={workspaces} />)}
        </div>
      </div>
    </PageContainer>
  );
}
